<template>
  <div class="smesco-fee-setting-form bg-white p-6 rounded-md shadow-sm">
    <div class="mb-6 border-b pb-4">
      <h2 class="text-xl font-semibold text-neutral-800">{{ isEditMode ? 'Edit Fee Setting' : 'Tambah Fee Setting' }}</h2>
    </div>
    
    <form @submit.prevent="submitForm">
      <div class="grid grid-cols-1 gap-6 mb-6">
        <div>
          <label class="block text-sm font-medium text-neutral-700 mb-2">Delivery Service (Ekspedisi) <span class="text-red-500">*</span></label>
          <div class="relative">
            <select v-model="form.delivery_service_id" @change="onDeliveryServiceChange" class="w-full border border-neutral-300 rounded-md px-4 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500" required>
              <option value="" disabled>Pilih Ekspedisi</option>
              <option v-for="service in deliveryServices" :key="service.id" :value="service.id">
                {{ service.name || service.id }}
              </option>
            </select>
          </div>
        </div>

        <div>
          <label class="block text-sm font-medium text-neutral-700 mb-2">Service Package <span class="text-red-500">*</span></label>
          <div class="relative">
            <select v-model="form.delivery_service_package_id" class="w-full border border-neutral-300 rounded-md px-4 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500" :disabled="!form.delivery_service_id || isLoadingPackages" required>
              <option value="" disabled>
                {{ isLoadingPackages ? 'Memuat Paket...' : (form.delivery_service_id ? 'Pilih Paket Layanan' : 'Pilih Ekspedisi Terlebih Dahulu') }}
              </option>
              <option v-for="pkg in deliveryServicePackages" :key="pkg.id" :value="pkg.id">
                {{ pkg.group_delivery || pkg.name || pkg.id }}
              </option>
            </select>
          </div>
        </div>

        <div>
          <label class="block text-sm font-medium text-neutral-700 mb-2">Fixed Fee (Rp)</label>
          <TextField 
            type="number" 
            v-model.number="form.fixed_fee" 
            placeholder="0" 
            :disabled="form.percentage_fee > 0"
            @input="handleFixedFeeInput"
          />
          <span v-if="form.percentage_fee > 0" class="text-xs text-neutral-500 mt-1 block">Dinonaktifkan karena Percentage Fee terisi.</span>
        </div>

        <div>
          <label class="block text-sm font-medium text-neutral-700 mb-2">Percentage Fee (%)</label>
          <TextField 
            type="number" 
            v-model.number="form.percentage_fee" 
            placeholder="0" 
            :disabled="form.fixed_fee > 0"
            @input="handlePercentageFeeInput"
          />
          <span v-if="form.fixed_fee > 0" class="text-xs text-neutral-500 mt-1 block">Dinonaktifkan karena Fixed Fee terisi.</span>
        </div>
      </div>

      <div class="flex justify-end gap-4 mt-8 pt-4 border-t border-neutral-200">
        <LinkButton buttonText="Batal" url="/smesco/fee-setting" linkType="internal" type="secondary" />
        <Button buttonText="Simpan" type="primary" :disabled="isLoading" submit />
      </div>
    </form>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import api from '@/api'

export default {
  components: {
    TextField: () => import(/* webpackChunkName: "Forms" */ '@/components/Forms/TextField'),
    Button: () => import(/* webpackChunkName: "Forms" */ '@/components/Forms/Button'),
    LinkButton: () => import(/* webpackChunkName: "Forms" */ '@/components/Forms/LinkButton')
  },
  data() {
    return {
      isEditMode: false,
      isLoading: false,
      isLoadingPackages: false,
      deliveryServices: [],
      deliveryServicePackages: [],
      form: {
        delivery_service_id: '',
        delivery_service_package_id: '',
        fixed_fee: 0,
        percentage_fee: 0
      }
    }
  },
  created() {
    this.fetchDeliveryServices()
    if (this.$route.params.id) {
      this.isEditMode = true
      this.fetchDetail(this.$route.params.id)
    }
  },
  methods: {
    ...mapActions('common', ['showLoading', 'hideLoading']),
    handleFixedFeeInput() {
      if (this.form.fixed_fee > 0) {
        this.form.percentage_fee = 0
      }
    },
    handlePercentageFeeInput() {
      if (this.form.percentage_fee > 0) {
        this.form.fixed_fee = 0
      }
    },
    fetchDeliveryServices() {
      api.partner.deliveryServices({ page: 1, limit: 100 })
        .then(res => {
          if (res.data && res.data.data) {
            this.deliveryServices = res.data.data.items || res.data.data || []
          } else {
             // Fallback if data format is different
             this.deliveryServices = res.data || []
          }
        })
        .catch(err => {
          console.error('Failed to fetch delivery services', err)
        })
    },
    fetchDetail(id) {
      this.showLoading()
      // Use detail endpoint `/mitra-fee-settings/{id}`
      api.smesco.feeSettingDetail(id)
        .then(res => {
          const detail = res.data?.data || res.data
          if (detail) {
            this.form.delivery_service_id = detail.delivery_service_id
            this.form.delivery_service_package_id = detail.delivery_service_package_id
            this.form.fixed_fee = detail.fixed_fee || 0
            this.form.percentage_fee = detail.percentage_fee || 0
            if (this.form.delivery_service_id) {
              this.fetchDeliveryServicePackages(this.form.delivery_service_id, this.form.delivery_service_package_id)
            }
          }
          this.hideLoading()
        })
        .catch(() => {
          this.hideLoading()
          this.$toast?.error('Gagal memuat data detail')
        })
    },
    onDeliveryServiceChange() {
      this.form.delivery_service_package_id = ''
      this.deliveryServicePackages = []
      if (this.form.delivery_service_id) {
        this.fetchDeliveryServicePackages(this.form.delivery_service_id)
      }
    },
    fetchDeliveryServicePackages(deliveryServiceId, preselectedPackageId = null) {
      this.isLoadingPackages = true
      api.partner.packages(deliveryServiceId)
        .then(res => {
          if (res.data && res.data.data) {
            this.deliveryServicePackages = res.data.data.items || res.data.data || []
          } else {
            this.deliveryServicePackages = res.data || []
          }
          if (preselectedPackageId) {
            this.form.delivery_service_package_id = preselectedPackageId
          }
        })
        .catch(err => {
          console.error('Failed to fetch delivery service packages', err)
          this.$toast?.error('Gagal memuat paket layanan')
        })
        .finally(() => {
          this.isLoadingPackages = false
        })
    },
    submitForm() {
      if (!this.form.delivery_service_id) {
        alert('Delivery Service ID wajib diisi')
        return
      }

      if (!this.form.delivery_service_package_id) {
        alert('Service Package wajib diisi')
        return
      }

      if (this.form.fixed_fee <= 0 && this.form.percentage_fee <= 0) {
        alert('Salah satu Fixed Fee atau Percentage Fee harus lebih dari 0')
        return
      }

      this.isLoading = true
      this.showLoading()

      const payload = {
        delivery_service_id: this.form.delivery_service_id,
        delivery_service_package_id: this.form.delivery_service_package_id,
        fixed_fee: Number(this.form.fixed_fee),
        percentage_fee: Number(this.form.percentage_fee)
      }

      const request = this.isEditMode
        ? api.smesco.updateFeeSetting(this.$route.params.id, payload)
        : api.smesco.createFeeSetting(payload)

      request
        .then(() => {
          this.hideLoading()
          this.isLoading = false
          this.$toast?.success(`Data berhasil di${this.isEditMode ? 'perbarui' : 'simpan'}`)
          this.$router.push('/smesco/fee-setting')
        })
        .catch(err => {
          this.hideLoading()
          this.isLoading = false
          this.$toast?.error(err.response?.data?.message || 'Terjadi kesalahan pada sistem')
        })
    }
  }
}
</script>
